import React, { useState } from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import { Switch } from '../ui/Switch';
import {
  FORM_VAT_RATES,
  formatTtcPreview,
  FORM_TEXT_KIND_OPTIONS,
  parseTextKind,
  parseQuestionRequired,
  questionAddsValueToTotalTTC,
  textKindLabel,
  emptyOption,
} from '../../utils/hubupNotificationForm';

const fieldClass =
  'rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const inputClass = `w-full ${fieldClass}`;
const vatSelectClass = `${fieldClass} w-[7rem] shrink-0`;

function isOptionMonetized(opt) {
  return opt?.monetize === true;
}

function PriceWithVatField({
  label,
  amount,
  vatRate,
  disabled,
  onAmountChange,
  onVatChange,
  amountPlaceholder = 'Optionnel',
  compact = false,
}) {
  const ttcPreview = formatTtcPreview(amount, vatRate);
  return (
    <div>
      {label ? (
        <label className="text-xs font-medium text-foreground mb-1 block">
          {label}
        </label>
      ) : null}
      <div className={`flex gap-2 ${compact ? 'flex-col sm:flex-row' : ''}`}>
        <input
          type="number"
          min="0"
          step="0.01"
          className={`${inputClass} ${compact ? 'sm:flex-1' : 'flex-1'}`}
          value={amount}
          disabled={disabled}
          placeholder={amountPlaceholder}
          onChange={(e) => onAmountChange(e.target.value)}
        />
        <select
          className={vatSelectClass}
          value={vatRate}
          disabled={disabled}
          onChange={(e) => onVatChange(e.target.value)}
          aria-label="Taux de TVA"
        >
          {FORM_VAT_RATES.map((r) => (
            <option key={r.value} value={r.value}>
              TVA {r.label}
            </option>
          ))}
        </select>
      </div>
      {ttcPreview ? (
        <p className="text-[10px] text-muted-foreground mt-1">
          TTC (affiché visiteur) : <span className="font-medium text-foreground">{ttcPreview}</span>
        </p>
      ) : null}
    </div>
  );
}

function parseQuestionType(raw) {
  if (raw === 'options') return 'options';
  if (raw === 'note') return 'note';
  return 'text';
}

function emptyQuestion(type = 'text') {
  const kind = parseQuestionType(type);
  if (kind === 'options') {
    return {
      type: 'options',
      questionName: '',
      required: true,
      optionsSelectable: 1,
      options: [emptyOption()],
    };
  }
  if (kind === 'note') {
    return {
      type: 'note',
      questionName: '',
      required: true,
      optionsSelectable: 1,
      options: [],
    };
  }
  return {
    type: 'text',
    questionName: '',
    textKind: 'short',
    addValueToTotalTTC: false,
    required: true,
    optionsSelectable: 1,
    options: [],
  };
}

function questionSummary(q) {
  const name = String(q?.questionName ?? '').trim() || 'Sans intitulé';
  const required = parseQuestionRequired(q?.required);
  const requiredLabel = required ? 'Obligatoire' : 'Facultatif';
  if (q?.type === 'options') {
    const opts =
      Array.isArray(q.options)
        ? q.options.filter((o) => String(o?.optionName ?? '').trim()).length
        : 0;
    return {
      name,
      typeLabel: 'Choix multiple',
      detail: `${requiredLabel}${opts > 0 ? ` · ${opts} opt.` : ''}`,
    };
  }
  if (q?.type === 'note') {
    return {
      name,
      typeLabel: 'Note (1 à 5)',
      detail: requiredLabel,
    };
  }
  const amountHint = questionAddsValueToTotalTTC(q) ? ' · montant TTC' : '';
  return {
    name,
    typeLabel: textKindLabel(q?.textKind),
    detail: `${requiredLabel}${amountHint}`,
  };
}

function remapCollapsedIndices(collapsed, from, to) {
  const next = new Set();
  for (const i of collapsed) {
    let ni = i;
    if (from < to) {
      if (i === from) ni = to;
      else if (i > from && i <= to) ni = i - 1;
    } else if (from > to) {
      if (i === from) ni = to;
      else if (i >= to && i < from) ni = i + 1;
    }
    next.add(ni);
  }
  return next;
}

function remapCollapsedAfterRemove(collapsed, removedIndex) {
  const next = new Set();
  for (const i of collapsed) {
    if (i < removedIndex) next.add(i);
    else if (i > removedIndex) next.add(i - 1);
  }
  return next;
}

export default function NotificationFormEditor({
  value,
  onChange,
  onBack,
  onRemove,
  disabled,
  showRemove,
}) {
  const form = value;
  const questions = Array.isArray(form?.questions) ? form.questions : [];
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [optionDrag, setOptionDrag] = useState(null);
  const [optionOver, setOptionOver] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const patch = (partial) => onChange({ ...form, ...partial });

  const isQuestionExpanded = (index) => !collapsed.has(index);

  const toggleQuestionExpanded = (index) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const collapseAllQuestions = () => {
    setCollapsed(new Set(questions.map((_, i) => i)));
  };

  const expandAllQuestions = () => {
    setCollapsed(new Set());
  };

  const patchQuestion = (index, partial) => {
    const next = questions.map((q, i) => (i === index ? { ...q, ...partial } : q));
    patch({ questions: next });
  };

  const addQuestion = () => {
    const nextIndex = questions.length;
    patch({ questions: [...questions, emptyQuestion('text')] });
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(nextIndex);
      return next;
    });
  };

  const removeQuestion = (index) => {
    if (questions.length <= 1) return;
    patch({ questions: questions.filter((_, i) => i !== index) });
    setCollapsed((prev) => remapCollapsedAfterRemove(prev, index));
  };

  const moveQuestion = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...questions];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    patch({ questions: next });
    setCollapsed((prev) => remapCollapsedIndices(prev, from, to));
  };

  const handleQuestionDragStart = (e, index) => {
    if (disabled) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleQuestionDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex !== index) setOverIndex(index);
  };

  const handleQuestionDrop = (e, toIndex) => {
    e.preventDefault();
    const from =
      dragIndex ?? Number.parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isFinite(from)) moveQuestion(from, toIndex);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleQuestionDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const patchOption = (qIndex, oIndex, partial) => {
    const q = questions[qIndex];
    const opts = Array.isArray(q.options) ? q.options : [];
    const nextOpts = opts.map((o, i) => (i === oIndex ? { ...o, ...partial } : o));
    patchQuestion(qIndex, { options: nextOpts });
  };

  const addOption = (qIndex) => {
    const q = questions[qIndex];
    const opts = Array.isArray(q.options) ? q.options : [];
    patchQuestion(qIndex, { options: [...opts, emptyOption()] });
  };

  const removeOption = (qIndex, oIndex) => {
    const q = questions[qIndex];
    const opts = Array.isArray(q.options) ? q.options : [];
    if (opts.length <= 1) return;
    const nextOpts = opts.filter((_, i) => i !== oIndex);
    const sel = Math.max(1, Number(q.optionsSelectable) || 1);
    patchQuestion(qIndex, {
      options: nextOpts,
      optionsSelectable: Math.min(sel, nextOpts.length),
    });
  };

  const moveOption = (qIndex, from, to) => {
    if (from == null || to == null || from === to) return;
    const q = questions[qIndex];
    const opts = Array.isArray(q.options) ? [...q.options] : [];
    if (from < 0 || to < 0 || from >= opts.length || to >= opts.length) return;
    const [item] = opts.splice(from, 1);
    opts.splice(to, 0, item);
    patchQuestion(qIndex, { options: opts });
  };

  const handleOptionDragStart = (e, qIndex, oIndex) => {
    if (disabled) return;
    setOptionDrag({ qIndex, oIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${qIndex}:${oIndex}`);
  };

  const handleOptionDragOver = (e, qIndex, oIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (optionDrag?.qIndex !== qIndex || optionDrag?.oIndex !== oIndex) {
      setOptionOver({ qIndex, oIndex });
    }
  };

  const handleOptionDrop = (e, qIndex, toIndex) => {
    e.preventDefault();
    let fromQ = optionDrag?.qIndex;
    let fromO = optionDrag?.oIndex;
    if (fromQ == null || fromO == null) {
      const raw = e.dataTransfer.getData('text/plain');
      const [qStr, oStr] = raw.split(':');
      fromQ = Number.parseInt(qStr, 10);
      fromO = Number.parseInt(oStr, 10);
    }
    if (fromQ === qIndex && Number.isFinite(fromO)) {
      moveOption(qIndex, fromO, toIndex);
    }
    setOptionDrag(null);
    setOptionOver(null);
  };

  const handleOptionDragEnd = () => {
    setOptionDrag(null);
    setOptionOver(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          onClick={onBack}
          disabled={disabled}
        >
          <Icon name="ArrowLeft" size={14} />
          Retour à la notification
        </button>
        {showRemove ? (
          <Button
            type="button"
            variant="outline"
            size="xs"
            iconName="Trash2"
            iconPosition="left"
            onClick={onRemove}
            disabled={disabled}
          >
            Retirer le formulaire
          </Button>
        ) : null}
      </div>

      <div>
        <label className="text-xs font-medium text-foreground mb-1 block">
          Nom du formulaire <span className="text-error">*</span>
        </label>
        <input
          type="text"
          className={inputClass}
          value={form.name}
          disabled={disabled}
          placeholder="Ex. Réponse à l’événement"
          onChange={(e) => patch({ name: e.target.value })}
        />
      </div>

      <div>
        <label className="text-xs font-medium text-foreground mb-1 block">
          Description
        </label>
        <textarea
          className={`${inputClass} min-h-[72px] resize-y`}
          value={form.description}
          disabled={disabled}
          placeholder="Instructions affichées aux visiteurs"
          onChange={(e) => patch({ description: e.target.value })}
        />
      </div>

      <PriceWithVatField
        label="Prix de base HT (€)"
        amount={form.priceAmount ?? ''}
        vatRate={form.priceVatRate ?? '20'}
        disabled={disabled}
        compact
        onAmountChange={(v) => patch({ priceAmount: v })}
        onVatChange={(v) => patch({ priceVatRate: v })}
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold text-foreground">Champs</p>
          <div className="flex items-center gap-2">
            {questions.length > 1 ? (
              <>
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                  onClick={expandAllQuestions}
                  disabled={disabled}
                >
                  Tout déplier
                </button>
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                  onClick={collapseAllQuestions}
                  disabled={disabled}
                >
                  Tout replier
                </button>
              </>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="xs"
              iconName="Plus"
              iconPosition="left"
              onClick={addQuestion}
              disabled={disabled}
            >
              Ajouter
            </Button>
          </div>
        </div>
        {questions.length > 1 ? (
          <p className="text-[11px] text-muted-foreground">
            Glissez un champ pour changer l&apos;ordre.
          </p>
        ) : null}

        {questions.map((q, qIndex) => {
          const expanded = isQuestionExpanded(qIndex);
          const summary = questionSummary(q);
          return (
          <div
            key={`q-${qIndex}`}
            onDragOver={(e) => handleQuestionDragOver(e, qIndex)}
            onDrop={(e) => handleQuestionDrop(e, qIndex)}
            className={`rounded-lg border bg-muted/20 transition-shadow ${
              expanded ? 'p-3 space-y-3' : 'p-2'
            } ${
              dragIndex === qIndex ? 'opacity-60 border-dashed border-primary' : 'border-border'
            } ${
              overIndex === qIndex && dragIndex !== qIndex
                ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                : ''
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                {questions.length > 1 ? (
                  <span
                    draggable={!disabled}
                    onDragStart={(e) => handleQuestionDragStart(e, qIndex)}
                    onDragEnd={handleQuestionDragEnd}
                    className="p-1 rounded text-muted-foreground hover:bg-muted cursor-grab active:cursor-grabbing shrink-0 touch-none"
                    title="Glisser pour réordonner"
                    aria-label={`Réordonner le champ ${qIndex + 1}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon name="GripVertical" size={16} />
                  </span>
                ) : null}
                <button
                  type="button"
                  className="flex items-center gap-2 min-w-0 flex-1 text-left rounded-md py-0.5 pr-1 hover:bg-muted/60 transition-colors"
                  onClick={() => toggleQuestionExpanded(qIndex)}
                  disabled={disabled}
                  aria-expanded={expanded}
                >
                  <Icon
                    name={expanded ? 'ChevronDown' : 'ChevronRight'}
                    size={16}
                    className="shrink-0 text-muted-foreground"
                  />
                  <span className="text-xs font-medium text-muted-foreground shrink-0">
                    C{qIndex + 1}
                  </span>
                  <span className="text-sm text-foreground truncate">
                    {summary.name}
                  </span>
                  {!expanded ? (
                    <span className="text-[10px] rounded-full border border-border bg-background px-1.5 py-0.5 text-muted-foreground shrink-0">
                      {summary.typeLabel}
                      {summary.detail ? ` · ${summary.detail}` : ''}
                    </span>
                  ) : null}
                </button>
              </div>
              {questions.length > 1 ? (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted text-error shrink-0"
                  title="Supprimer le champ"
                  disabled={disabled}
                  onClick={() => removeQuestion(qIndex)}
                >
                  <Icon name="Trash2" size={14} />
                </button>
              ) : null}
            </div>

            {expanded ? (
              <>
            <div>
              <label className="text-xs font-medium text-foreground mb-1 block">
                Type
              </label>
              <select
                className={inputClass}
                value={parseQuestionType(q.type)}
                disabled={disabled}
                onChange={(e) => {
                  const type = parseQuestionType(e.target.value);
                  const base = emptyQuestion(type);
                  patchQuestion(qIndex, { ...base, questionName: q.questionName });
                }}
              >
                <option value="text">Texte</option>
                <option value="options">Choix multiple</option>
                <option value="note">Note (étoiles 1 à 5)</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground mb-1 block">
                Intitulé <span className="text-error">*</span>
              </label>
              <input
                type="text"
                className={inputClass}
                value={q.questionName}
                disabled={disabled}
                onChange={(e) => patchQuestion(qIndex, { questionName: e.target.value })}
              />
            </div>

            <Switch
              checked={parseQuestionRequired(q.required)}
              disabled={disabled}
              label="Champ obligatoire"
              description={
                q.type === 'options'
                  ? 'Si activé, le visiteur doit cocher au moins une case (jusqu’au maximum ci-dessous).'
                  : q.type === 'note'
                    ? 'Si activé, le visiteur doit choisir une note entre 1 et 5 étoiles.'
                    : 'Si désactivé, le visiteur peut laisser ce champ vide.'
              }
              onChange={(e) =>
                patchQuestion(qIndex, { required: e.target.checked })
              }
            />

            {q.type === 'note' ? (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Le visiteur choisit une note de 1 à 5 étoiles.
              </p>
            ) : null}

            {q.type === 'text' ? (
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Format du texte
                </label>
                <select
                  className={inputClass}
                  value={parseTextKind(q.textKind)}
                  disabled={disabled}
                  onChange={(e) => {
                    const textKind = e.target.value;
                    const canAmount =
                      textKind === 'short' || textKind === 'number';
                    patchQuestion(qIndex, {
                      textKind,
                      addValueToTotalTTC: canAmount
                        ? q.addValueToTotalTTC === true
                        : false,
                    });
                  }}
                >
                  {FORM_TEXT_KIND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {q.type === 'text' &&
            (parseTextKind(q.textKind) === 'short' ||
              parseTextKind(q.textKind) === 'number') ? (
              <Switch
                checked={q.addValueToTotalTTC === true}
                disabled={disabled}
                label="Montant TTC ajouté au total"
                description="La valeur saisie par le visiteur (nombre) s’additionne au prix TTC du formulaire et des options, comme une participation."
                onChange={(e) =>
                  patchQuestion(qIndex, {
                    addValueToTotalTTC: e.target.checked,
                  })
                }
              />
            ) : null}

            {q.type === 'options' ? (
              <div className="space-y-3 pl-0 sm:pl-1">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-xs font-medium text-foreground">
                      Maximum de cases cochables
                    </label>
                    <span className="text-xs font-semibold tabular-nums text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      {q.optionsSelectable ?? 1}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max={Math.max(1, (Array.isArray(q.options) ? q.options : []).length)}
                    step="1"
                    className="w-full h-1.5 accent-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    value={Math.min(
                      q.optionsSelectable ?? 1,
                      Math.max(1, (Array.isArray(q.options) ? q.options : []).length),
                    )}
                    disabled={disabled}
                    onChange={(e) =>
                      patchQuestion(qIndex, {
                        optionsSelectable: Math.max(1, Number(e.target.value) || 1),
                      })
                    }
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Le visiteur peut cocher jusqu&apos;à {q.optionsSelectable ?? 1} case
                    {(q.optionsSelectable ?? 1) === 1 ? '' : 's'}.
                    {parseQuestionRequired(q.required)
                      ? ' Au moins 1 case obligatoire.'
                      : ' Aucune case obligatoire.'}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-foreground">Options</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => addOption(qIndex)}
                    disabled={disabled}
                  >
                    Option
                  </Button>
                </div>
                {(Array.isArray(q.options) ? q.options : []).length > 1 ? (
                  <p className="text-[10px] text-muted-foreground">
                    Glissez une option pour changer l&apos;ordre.
                  </p>
                ) : null}
                {(Array.isArray(q.options) ? q.options : []).map((opt, oIndex) => {
                  const monetized = isOptionMonetized(opt);
                  const optionCount = q.options?.length ?? 0;
                  const isDragging =
                    optionDrag?.qIndex === qIndex && optionDrag?.oIndex === oIndex;
                  const isOver =
                    optionOver?.qIndex === qIndex &&
                    optionOver?.oIndex === oIndex &&
                    !isDragging;
                  return (
                  <div
                    key={`opt-${qIndex}-${oIndex}`}
                    onDragOver={(e) => handleOptionDragOver(e, qIndex, oIndex)}
                    onDrop={(e) => handleOptionDrop(e, qIndex, oIndex)}
                    className={`rounded-md border bg-background/60 p-3 space-y-2 transition-shadow ${
                      isDragging
                        ? 'opacity-60 border-dashed border-primary'
                        : 'border-border'
                    } ${
                      isOver
                        ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                        : ''
                    }`}
                  >
                    <div className="flex items-end gap-2">
                      {optionCount > 1 ? (
                        <span
                          draggable={!disabled}
                          onDragStart={(e) => handleOptionDragStart(e, qIndex, oIndex)}
                          onDragEnd={handleOptionDragEnd}
                          className="p-1 mb-1 rounded text-muted-foreground hover:bg-muted cursor-grab active:cursor-grabbing shrink-0 touch-none"
                          title="Glisser pour réordonner"
                          aria-label={`Réordonner l’option ${oIndex + 1}`}
                        >
                          <Icon name="GripVertical" size={16} />
                        </span>
                      ) : null}
                      <div className="flex-1 min-w-0">
                        <label className="text-[10px] text-muted-foreground mb-0.5 block">
                          Libellé
                          {optionCount > 1 ? (
                            <span className="text-muted-foreground/70">
                              {' '}
                              · Option {oIndex + 1}
                            </span>
                          ) : null}
                        </label>
                        <input
                          type="text"
                          className={inputClass}
                          value={opt.optionName}
                          disabled={disabled}
                          onChange={(e) =>
                            patchOption(qIndex, oIndex, { optionName: e.target.value })
                          }
                        />
                      </div>
                      {(q.options?.length ?? 0) > 1 ? (
                        <button
                          type="button"
                          className="p-2 rounded hover:bg-muted text-error shrink-0"
                          disabled={disabled}
                          title="Supprimer l’option"
                          onClick={() => removeOption(qIndex, oIndex)}
                        >
                          <Icon name="Trash2" size={14} />
                        </button>
                      ) : null}
                    </div>
                    <Switch
                      checked={monetized}
                      disabled={disabled}
                      label="Voulez-vous monétiser ce choix ?"
                      onChange={(e) => {
                        if (e.target.checked) {
                          patchOption(qIndex, oIndex, {
                            monetize: true,
                            priceAmount: opt.priceAmount || '',
                            priceVatRate: opt.priceVatRate || '20',
                          });
                        } else {
                          patchOption(qIndex, oIndex, {
                            monetize: false,
                            priceAmount: '',
                            priceVatRate: '20',
                          });
                        }
                      }}
                    />
                    {monetized ? (
                      <PriceWithVatField
                        label="Prix supplémentaire HT (€)"
                        amount={opt.priceAmount ?? ''}
                        vatRate={opt.priceVatRate ?? '20'}
                        disabled={disabled}
                        compact
                        amountPlaceholder="0,00"
                        onAmountChange={(v) =>
                          patchOption(qIndex, oIndex, { priceAmount: v })
                        }
                        onVatChange={(v) =>
                          patchOption(qIndex, oIndex, { priceVatRate: v })
                        }
                      />
                    ) : null}
                  </div>
                );
                })}
              </div>
            ) : null}
              </>
            ) : null}
          </div>
        );
        })}
      </div>
    </div>
  );
}
